import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { AppConfig } from '../../app.config';
import { HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';

export class PortalUser {
    Id: string;
    Name: string;
    Email: string;
    Phone: string;
    IsActive: boolean;
    GravatarHash: string;
    Role: string;
}

export class PortalCompany {
    Id: string;
    Name: string;
    SupplierStatus?: string;
    LastSupplierStatusCheck?: string;
    BusinessId?: string;
}

export class PortalCompanyWithRole extends PortalCompany {
    Role: string;
}

export class InvoiceOperator {
    Id: string;
    Name: string;
    OperatorIdentifier: string;
    IdentifierAndName: string;
}

export interface UserRightGroup {
    ObjectId: string;
    CompanyId: string;
    ClientId: number;
    Name: string;
    Description: string;
    Type: number;
    SSoGroupName: string;
    AllCompanyUsers: boolean;
}

export interface EndCustomerSearchProperty {
    propertyName: string;
    minLength: number;
}

export interface ExternalContactSearchConfiguration {
    roles: string[];
    searchProperties: EndCustomerSearchProperty[];
}

export interface ExternalContactResult {
    dataSource: string;
    personName: string;
    businessId: string;
    customerId: string;
    phoneNumber: string;
    email: string;
    streetAddress: string;
    postCode: string;
    postOffice: string;
}

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private urlPortalUsersBackend: string = this.config.backendPortalUsersUri + 'WebAPI/api/';
    private urlCompanyUsers: string = 'User/CompanyUsers';
    private urlCompanies: string = 'User/Companies';
    private urlProductRoleEnabled: string = 'User/IsProductRoleEnabled?productId={0}&role={1}';
    private urlProductRoleWithLimitEnabled: string = 'User/IsProductRoleEnabledWithLimit?productId={0}&role={1}';
    private urlCompanyById: string = "User/GetCompanyById?companyId={0}";
    private personsUrl: string = this.config.backendPortalUsersUri + 'WebAPI/odata/UserAdministrator({userId})';
    private invoiceOperatorUrl: string = 'InvoiceOperators';

    private _externalPersonSubject$ = new BehaviorSubject<boolean>(false);
    readonly externalPersonStatus$ = this._externalPersonSubject$.asObservable();

    public deputies$: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);

    private customerAdminApiBaseUrl: string;
    private externalContactApiBaseUrl: string;

    private productId: number;

    constructor(
        private http: HttpClient,
        private config: AppConfig,
        private oAuthService: OAuthService) {

        this.customerAdminApiBaseUrl = this.config.backendIwmUri + 'CustomerAdminApi/';
        this.externalContactApiBaseUrl = this.config.backendExternalContactUri + 'ExternalContactProxy/api/';
        this.productId = this.config.productId;
    }

    /**
     * Checks if user has a given role claim.
     * 
     * @param role Role claim to check
     * @returns A boolean indicating if user has a given role claim
     */
    public userHasRoleClaim(role: string) {
        const roles = this.oAuthService.getIdentityClaims()?.['role'];

        if (!roles) {
            return false;
        }

        return Array.isArray(roles) ?
            roles.includes(role) :
            roles === role;
    }

    public getProductRoles(): Observable<any> {
        const url = this.config.backendPortalUsersUri +
            `WebAPI/api/User/ProductRoles?productId=${this.config.productId}`;

        return this.fetchData(url)
            .pipe(
                map(results => results.filter(o => o.ProductId === this.config.productId))
            );
    }

    /**
     * Get current user's company users
     */
    public getCompanyUsers(): Observable<Array<PortalUser>> {
        const url = this.urlPortalUsersBackend + this.urlCompanyUsers;
        return this.fetchData(url);
    }

    public getAllCompanies(): Observable<Array<PortalCompany>> {
        const url = this.urlPortalUsersBackend + this.urlCompanies;
        return this.fetchData(url);
    }

    public setDeputies(value) {
        this.deputies$.next(value);
    }

    /**
     *  Get if given role on a given product is enabled for current user
     */
    public isProductRoleEnabled(product: string, role: string): Observable<any> {
        const url = this.urlPortalUsersBackend + this.urlProductRoleEnabled.replace('{0}', product).replace('{1}', role);
        return this.fetchData(url);
    }

    /**
     *  Get if given role on a given product is enabled for current user
     */
    public isProductRoleWithLimitEnabled(product: string, role: string): Observable<any> {
        const url = this.urlPortalUsersBackend + this.urlProductRoleWithLimitEnabled.replace('{0}', product).replace('{1}', role);
        return this.fetchData(url);
    }

    /**
     * Get user company by id
     */
    public getCompanyById(id: string) {
        const url = this.urlPortalUsersBackend + this.urlCompanyById.replace('{0}', id);
        return this.fetchData(url);
    }

    private fetchData(url: string): Observable<any> {
        return this.http.get(url);
    }

    /*
    * Get current user identity from account api
    * @param userId (sub)
    * @returns single result in list
    */
    public getUser(userId: string): Observable<any> {
        const requestUrl = this.personsUrl
            .replace('{userId}', userId);

        return this.http.get(requestUrl);
    }

    /**
     * Gets user right groups for the current company.
     * 
     * User must be in IWM admin role.
     * 
     * @returns User right groups for the current company
     */
    public getUserRightGroups(): Observable<UserRightGroup[]> {
        const url = this.customerAdminApiBaseUrl + 'UserRightGroups';

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as UserRightGroup[])
            );
    }


    public getInvoiceOperators(): Observable<InvoiceOperator[]> {
        const url = this.customerAdminApiBaseUrl + this.invoiceOperatorUrl;

        return this.http.get<any>(url)
            .pipe(
                map(result => result.value as InvoiceOperator[])
            );
    }

    /**
     * Checks which end customer searches (if any) are available for the current user.
     * 
     * @param ownerCompanyId id of the company that owns the target (Task)
     * @returns Array of available search configurations.
     */
    public getAvailableEndCustomerSearches(ownerCompanyId: string): Observable<Array<ExternalContactSearchConfiguration>> {
        const url = this.externalContactApiBaseUrl + `ExternalContact/AvailableSearches?proxyOwnerId=${ownerCompanyId}&productId=${this.productId}`;

        return this.http.get<Array<ExternalContactSearchConfiguration>>(url);
    }

    /**
     * Searches end customers by search query.
     * 
     * @param query Search filter string
     * @param role Role that end customers are searched for (EndCustomer, PayerCustomer etc.)
     * @param searchParametere Property used for filtering (PersonName, CustomerId etc.)
     * @param proxyOwnerId id of the company that owns the target (Task)
     * @returns End customers found with given search query
     */
    public searchEndCustomers(query: string, role: string, searchParameter: string, proxyOwnerId: string): Observable<ExternalContactResult[]> {
        const url = this.externalContactApiBaseUrl + 'ExternalContact/Search';

        return this.http.get<ExternalContactResult[]>(url, {
            params: {
                q: query,
                role: role,
                searchParameter: searchParameter,
                proxyOwnerId: proxyOwnerId,
                productId: this.productId
            }
        });
    }

    public getActiveDeputies(type: string, id: string): Observable<any[]> {
        let url = this.config.backendIwmUri;
        switch (type) {
            case 'Task':
                url += 'Api/Tasks(' + id + ')/GetActivePersonStatuses';
                break;
            case 'Worksite':
                url += 'WorksiteApi/Worksites(' + id + ')/GetActivePersonStatuses';
                break;
            case 'Plan':
                url += 'PlanApi/Plans(' + id + ')/GetActivePersonStatuses';
                break;
            case 'CollectionInvoicingProposal':
                url += 'InvoicingProposalApi/CollectionInvoicingProposals(' + id + ')/GetActivePersonStatuses';
                break;
            case 'InvoicingProposal':
                url += 'InvoicingProposalApi/InvoicingProposals(' + id + ')/GetActivePersonStatuses';
                break;
            case 'Tendering':
                url += 'TenderingApi/Tenderings(' + id + ')/GetActivePersonStatuses';
                break;
            case 'Bid':
            case 'selectedBid':
                url += 'TenderingApi/Bids(' + id + ')/GetActivePersonStatuses';
                break;
            case 'EndCustomerInvoice':
                url += 'EndCustomerInvoiceApi/EndCustomerInvoices(' + id + ')/GetActivePersonStatuses';
                break;
        }
        return this.http.get<any>(url)
            .pipe(
                map(result => result.value)
            );
    }

}
